/**
 * [BLURY] | 🥭 useCanvasRenderer
 * Binds the framework-free renderer to a canvas element. It subscribes to the
 * store directly rather than re-rendering React on every slider tick, and
 * coalesces work into one rAF per frame.
 * @build 13.09.26
 * @updated 13.09.26 PHT
 * @author TONYTONY Sàrl
 */
import { useEffect, useRef, type RefObject } from 'react';
import { GrainFieldRenderer } from '@/engine/renderer';
import { useStudio } from '@/state/useStudio';

export function useCanvasRenderer(canvasRef: RefObject<HTMLCanvasElement | null>): void {
  const rendererRef = useRef<GrainFieldRenderer | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      console.warn('useCanvasRenderer: no canvas mounted, skipping');
      return;
    }

    const renderer = new GrainFieldRenderer();
    rendererRef.current = renderer;

    let frame = 0;
    const draw = () => {
      frame = 0;
      const { composition, previewSize, seed } = useStudio.getState();
      renderer.render(canvas, composition, previewSize, seed);
    };
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(draw);
    };

    draw();
    const unsubscribe = useStudio.subscribe(schedule);

    return () => {
      unsubscribe();
      if (frame) cancelAnimationFrame(frame);
      rendererRef.current = null;
    };
  }, [canvasRef]);
}
